import React, { useCallback, useRef, createRef, useMemo, useEffect, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { AgGridReact } from 'ag-grid-react';
import { createUseStyles } from '@Sage/hoi-poi-ui';
import Dinero from 'dinero.js';

import { publish } from 'lib/EventBuser';
import { DATA_GRID_REFRESH_TOTAL } from 'lib/events';

import { useWeb5Context } from 'lib/Web5Context';
import TextCell from './cells/TextCell';
import TextTwoValuesCell from './cells/TextTwoValuesCell';
import AvatarCell from './cells/AvatarCell';
import DateCell from './cells/DateCell';
import CurrencyCell from './cells/CurrencyCell';
import ValuePercentageCell from './cells/ValuePercentageCell';
import PercentageCell from './cells/PercentageCell';
import PercentageTextCell from './cells/PercentageTextCell';
import BooleanCell from './cells/BooleanCell';
import BooleanIconCell from './cells/BooleanIconCell';
import NumberCell from './cells/NumberCell';
import RowNumberCell from './cells/RowNumberCell';
import ToggleCell from './cells/ToggleCell';
import TagCell from './cells/TagCell';
import IconCell from './cells/IconCell';
import LoadingCell from './cells/LoadingCell';
import MultiValueCell from './cells/MultiValueCell';
import StepCell from './cells/StepCell';
import FilterCell from './cells/FilterCell';
import FullWidthDividerCell from './cells/FullWidthDividerCell';
import TextFilter from './filters/TextFilter';
import HeaderTextCell from './headerCells/HeaderTextCell';
import HeaderIconCell from './headerCells/HeaderIconCell';

import LoadingOverlay from './LoadingOverlay';
import NoRowsOverlay from './NoRowsOverlay';
import ToolTip from './components/Tooltip';
import Popover from './components/Popover';
import Actions from './components/Actions';
import MenuOptions from './components/MenuOptions';
import ModalOptions from '../ModalOptions';

import { addRequestToQueue, addToRowsBeforeDrag, processDragQueue } from 'utils/requests';
import { isEqual } from 'utils/objects';
import { getSvgSelected, getSvgNonSelected } from 'utils/domElements';

import styles from './styles/index';
const useStyles = createUseStyles(styles, 'DataGrid');

// Enabling input navigation
const suppressNavigation = (params) => {
    return params.event.key === 'Tab' || params.event.key?.includes('Arrow');
};

const defaultOptions = {
    rowHeight: 50,
    headerHeight: 40,
    floatingFiltersHeight: 40,
    enableCellTextSelection: true,
    suppressRowClickSelection: true,
    suppressCellFocus: true,
    suppressMovableColumns: true,
    suppressClickEdit: true,
    suppressScrollOnNewData: true,
    suppressContextMenu: true,
    suppressDragLeaveHidesColumns: true,
    rowDataChangeDetectionStrategy: 'IdentityCheck',
    defaultColDef: {
        resizable: true,
        floatingFilter: false,
    },
    columnDefs: [],
    rowData: [],
    reactNext: true,
    suppressKeyboardEvent: suppressNavigation,
    suppressTouch: true,
};

function DataGrid({
    id,
    className,
    dataSource,
    isLoading,
    rowData,
    totalRowData,
    hasError,
    emptyViewProps,
    errorViewProps,
    customEmptyViewComponent,
    initTable,
    initOnReady,
    useLazyLoad,
    infiniteScrolling,
    useLazyWithSections,
    pageSize,
    infiniteInitialRowCount,
    defaultColDef = {},
    columnDefs,
    useDragRows = false,
    useDragCustom = false,
    useDragColumns = false,
    customDragPreview,
    previewKey,
    getCustomDragInfo,
    canDrag,
    canDrop,
    onDragDrop,
    saveColumnsOrder,
    savePinnedColumns,
    saveColumnSize,
    saveVisibleColumns,
    useSort,
    getInitialSortInfo,
    changeSortDirection,
    onCancelGroupRequests,
    dragGroups,
    onDragEnter,
    onDragEnd,
    columnsRefreshOnDragEnd,
    onClickRow,
    fullWidthCellRenderer,
    fieldSelector,
    fieldSelectorProps,
    activeRowId,
    checkedRows,
    disabledCheckboxes,
    getCheckedRows,
    favoriteRows,
    disabledFavorites,
    getFavoriteRows,
    actions,
    hideActions,
    useFullWidthDivider,
    customIsFullWidthCell,
    getForceRedrawRows,
    addDropZones,
    showGhostWhenDrag = false,
    onRef,
    rowClass = '',
    rowClassRules = {},
    getTotal,
    forceGetTotal,
    locale,
    modalOptionsProps = {},
    useLoaderPlaceholder,
    loaderPlaceholderRowHeight,
    loaderPlaceholderRows,
    loaderPlaceholderColumns,
    useLoaderPlaceholderFullHeight,
    placeholderOptions,
    tooltipMenuContent,
    intercomRef,
    forceActionsRefresh,
    getRowNodeId,
    withoutOffset,
    forceRowUpdating,
    vanillaComponents = {},
    components = {},
    ...props
}) {
    //For lazyLoad --> Add fullWidthRows to the total, so the table will consider those rows
    // as well to the total rows to be printed.
    const getTotalWithFullWidthRows = useCallback(
        (rowData, totalData) => {
            if (!rowData) return totalData || 0;
            if (!useFullWidthDivider) return totalData || rowData.length;

            const fullWidthRows = rowData.filter((current) => current.isFullWidth);
            let totalFullWidthRows = fullWidthRows.length;
            if (totalData) return totalData + totalFullWidthRows;
            else return rowData.length;
        },
        [useFullWidthDivider],
    );

    //For general use --> Removes fullWidthRows from total, so them won't be counted
    // to show the total in the header info
    const getTotalWithoutFullWidthRows = useCallback(
        (rowData, totalData) => {
            if (!rowData) return totalData || 0;
            if (!useFullWidthDivider) return totalData || rowData.length;

            const fullWidthRows = rowData.filter((current) => current.isFullWidth);
            let totalFullWidthRows = fullWidthRows.length;
            return rowData.length - totalFullWidthRows;
        },
        [useFullWidthDivider],
    );

    const { t } = useWeb5Context();
    const classes = useStyles();
    const rootClassName = classnames(classes.root, className, 'ag-theme-balham');
    const dataGridRef = useRef(null);
    const gridRef = useRef(null);
    const gridWidthRef = useRef(0);
    const context = useWeb5Context();
    const apiRef = useRef(null);
    const isDragging = useRef(false);
    const gridApi = useRef(null);
    const gridColumnApi = useRef(null);
    const columnMovedId = useRef(null);
    const immutableRowData = useRef([]);
    const rowMoved = useRef(false);
    const immutableBeforeDrag = useRef([]);
    const isQueueProcessing = useRef(false);
    const [hoveredRowInfo, setHoveredRowInfo] = useState(null);
    const gridContext = useRef({
        tableId: id || '',
        tableRef: createRef(),
        totalData: totalRowData || getTotalWithoutFullWidthRows(rowData) || 0,
        checkboxData: checkedRows || null,
        favoriteData: favoriteRows || null,
        getHoveredRowInfo: setHoveredRowInfo,
        hasActions: actions && actions?.length > 0,
        tableData: rowData || [],
        tableDataForLazySections: [],
        updateTableData: (data) => (gridContext.current.tableData = data),
        useDragCustom,
        canDrag,
        t,
        hasShowTotal: false,
        locale: locale || '',
    });
    const oldInitialActiveRowId = useRef(null);
    const sortInfoForLazyLoad = useRef(null);
    const [suppressNoRowsOverlay, setSuppressNoRowsOverlay] = useState(true);
    const [hiddenColumns, setHiddenColumns] = useState(null);
    const [isLazyLoaded, setIsLazyLoaded] = useState(false);
    const defaultColumnMinWidth = 100;
    const lazyLoadInfo = useRef(null);
    const [mappedSchema, setMappedSchema] = useState([]);
    const [selectedFields, setSelectedFields] = useState({});
    const isFirstLoad = useRef(true);
    const oldSelectedFields = useRef({});
    const dragObserver = useRef();
    const dragTarget = useRef();

    //TODO set the currencyISO depending on the locale
    const setupCurrencyLocaleUser = useCallback((locale, currencyISO = '', currencySymbol = '') => {
        Dinero.globalLocale = locale;
        Dinero.defaultCurrency = currencyISO;
        Dinero.defaultUserCurrencySymbol = currencySymbol;
    }, []);

    // We need immutable data for row drag and drop actions
    useEffect(() => {
        if (rowData?.length > 0) {
            immutableRowData.current = [...rowData];
            gridContext.current.tableData = rowData;
        }
        if (!gridContext.current.hasShowTotal && !forceGetTotal && !totalRowData) return;

        const previousTotal = gridContext.current.totalData;
        const currentTotal = getTotalWithoutFullWidthRows(rowData) || 0;

        if (previousTotal !== currentTotal) {
            gridContext.current.totalData = currentTotal;
            publish(`${DATA_GRID_REFRESH_TOTAL}-${id}`, currentTotal);
        }
    }, [rowData, getTotalWithoutFullWidthRows, id, forceGetTotal, totalRowData]);

    // Get hidden columns
    useEffect(() => {
        if (fieldSelector) {
            if (columnDefs && columnDefs.length > 0) {
                const finalHiddenColumns = columnDefs.reduce((obj, column) => {
                    if (!column.suppressFromFieldSelector) {
                        obj[column.colId] = {
                            name: column.headerName,
                            hide: !!column.hide,
                        };
                    }
                    return obj;
                }, {});
                setHiddenColumns(finalHiddenColumns);
            }
        }
    }, [columnDefs, fieldSelector]);

    const shouldHideGhostRows = useCallback((hide) => {
        if (!gridApi.current) return;
        const bodyViewport = gridApi.current.gridBodyCtrl?.eBodyViewport;
        if (bodyViewport) bodyViewport.style.opacity = hide ? 0 : 'inherit';
    }, []);

    // Infinite row model: sort triggers a new block request that can be debounced (blockLoadDebounceMillis).
    // Before getRows runs, the grid may paint one empty placeholder row — hide the body and show the overlay immediately.
    const onLazyLoadSortChanged = useCallback(
        (event) => {
            if (!useLazyLoad) return;
            shouldHideGhostRows(true);
            gridContext.current.startRow = 0;
            event.api.showLoadingOverlay();
        },
        [useLazyLoad, shouldHideGhostRows],
    );

    // Lazy (infinite) tables: purge cache → getRows → shouldHideGhostRows(false) in setGridDataSource.
    // Non-lazy (rowData from Redux): purgeInfiniteCache does not refetch; hiding ghost rows would leave the body at opacity 0.
    const refresh = useCallback(
        (lazyInfo) => {
            lazyLoadInfo.current = lazyInfo;
            if (lazyLoadInfo?.current?.resetOffset) {
                gridApi?.current?.ensureIndexVisible(0, 'top');
            }

            if (!useLazyLoad) {
                shouldHideGhostRows(false);
                const startRow = lazyInfo?.resetOffset ? 0 : undefined;
                initTable?.(pageSize, startRow, null);
                gridApi?.current?.hideOverlay();
                return;
            }

            gridApi?.current?.purgeInfiniteCache();
            shouldHideGhostRows(true);

            // When there are no rows and the noRowsOverlay is being shown and we retrieve new data, in orther to
            // >> prevent a flickering and remove the noRowsOverlay, we execute hideOverlay;
            gridApi?.current?.hideOverlay();
        },
        [shouldHideGhostRows, useLazyLoad, initTable, pageSize],
    );

    const refreshCheckboxes = useCallback(
        (colId, areAllChecked) => {
            const tableId = id;
            const tableElement = document.getElementById(tableId);
            const checkboxCells = tableElement.querySelectorAll(`[col-id='${colId}']`);
            checkboxCells.forEach((cell) => {
                if (cell.classList.contains('w5-header')) return;
                const cellWrapper = cell.querySelector('.w5-cell');

                if (!cellWrapper) return;

                const rowId = cellWrapper.getAttribute('data-rowid');
                const cellCheckboxContainer = cellWrapper.querySelector('.w5-cell-checkbox');

                if (!cellCheckboxContainer) return;

                const isVisible = cellCheckboxContainer.classList.contains(
                    'w5-cell-checkbox--visible',
                );
                const isHidden = cellCheckboxContainer.classList.contains(
                    'w5-cell-checkbox--hidden',
                );
                const isBlocked = cellCheckboxContainer.classList.contains(
                    'w5-cell-checkbox--blocked',
                );
                const isDisabled = cellCheckboxContainer.classList.contains(
                    'w5-cell-checkbox--disabled',
                );
                if (isVisible || isHidden) {
                    if (areAllChecked) {
                        cellCheckboxContainer.classList.remove('w5-cell-checkbox--hidden');
                        cellCheckboxContainer.classList.add('w5-cell-checkbox--visible');
                    } else {
                        cellCheckboxContainer.classList.remove('w5-cell-checkbox--visible');
                        cellCheckboxContainer.classList.add('w5-cell-checkbox--hidden');
                    }
                }

                const svgContainer = cellWrapper.querySelector('.w5-cell-checkbox__svg-container');
                if (!svgContainer || disabledCheckboxes?.[colId]?.[rowId] || isDisabled) return;

                if (areAllChecked) {
                    if (isBlocked) return;
                    const svgSelected = getSvgSelected(tableId, colId, rowId);
                    if (!svgSelected) return;
                    svgContainer.innerHTML = svgSelected;
                    cellCheckboxContainer.setAttribute('data-ischecked', true);
                } else {
                    const svgNonSelected = getSvgNonSelected(tableId, colId, rowId);
                    if (!svgNonSelected) return;
                    svgContainer.innerHTML = svgNonSelected;
                    cellCheckboxContainer.setAttribute('data-ischecked', false);
                }
            });
        },
        [disabledCheckboxes, id],
    );

    const clearCheckboxes = useCallback(() => {
        if (!gridContext?.current?.checkboxData) return;
        const newCheckboxData = Object.keys(gridContext.current.checkboxData).reduce(
            (obj, current) => {
                refreshCheckboxes(current, false);

                obj[current] = {
                    rows: {},
                    areAllChecked: false,
                    lazyAreAllChecked: false,
                };
                return obj;
            },
            {},
        );

        gridContext.current.checkboxData = newCheckboxData;
        gridApi.current?.refreshHeader();
    }, [refreshCheckboxes]);

    const newGetCheckedRows = useCallback(() => {
        if (!getCheckedRows) return;
        const newChecked = getCheckedRows(immutableRowData.current);
        const checkboxData = gridContext?.current?.checkboxData || {};

        if (
            !checkboxData ||
            !newChecked ||
            Object.keys(newChecked).length === 0 ||
            isEqual(checkboxData, newChecked) ||
            !gridColumnApi.current
        ) {
            return;
        }

        let columns = Object.keys(newChecked).reduce((arr, current) => {
            const oldColumn = gridColumnApi.current.getColumn(current);
            if (oldColumn) arr.push(oldColumn);
            return arr;
        }, []);

        const finalChecked = checkboxData;

        Object.keys(newChecked).forEach((column) => {
            if (!finalChecked[column]) finalChecked[column] = newChecked[column];
            else {
                finalChecked[column].areAllChecked = newChecked[column].areAllChecked;
                finalChecked[column].lazyAreAllChecked = newChecked[column].lazyAreAllChecked;
                if (!finalChecked[column].rows) newChecked[column].rows;
                else {
                    finalChecked[column].rows = {
                        ...finalChecked[column].rows,
                        ...newChecked[column].rows,
                    };
                }
            }
        });

        gridContext.current.checkboxData = finalChecked;

        gridApi.current?.refreshCells({ columns, force: true });
        gridApi.current?.refreshHeader();
    }, [getCheckedRows]);

    const newGetFavoriteRows = useCallback(
        (rowData) => {
            if (!getFavoriteRows) return;

            let newFavorite;
            if (rowData) {
                newFavorite = getFavoriteRows(rowData);
            } else newFavorite = getFavoriteRows(immutableRowData.current);

            const data = gridContext?.current?.favoriteData || {};

            if (
                !data ||
                !newFavorite ||
                Object.keys(newFavorite).length === 0 ||
                isEqual(data, newFavorite)
            )
                return;

            let columns;
            if (gridColumnApi.current) {
                columns = Object.keys(newFavorite)?.reduce((arr, current) => {
                    const oldColumn = gridColumnApi.current.getColumn(current);
                    if (oldColumn) arr.push(oldColumn);
                    return arr;
                }, []);
            }

            const finalFavorite = data;

            Object.keys(newFavorite).forEach((column) => {
                if (!finalFavorite[column]) finalFavorite[column] = newFavorite[column];
                else {
                    if (!finalFavorite[column].rows) newFavorite[column].rows;
                    else {
                        finalFavorite[column].rows = {
                            ...finalFavorite[column].rows,
                            ...newFavorite[column].rows,
                        };
                    }
                }
            });

            gridContext.current.favoriteData = finalFavorite;
            if (columns) gridApi.current.refreshCells({ columns, force: true });
        },
        [getFavoriteRows],
    );

    const updateRow = useCallback((id, fields) => {
        const rowNode = gridApi.current.getRowNode(String(id));
        if (!rowNode) return;

        let data = rowNode.data;
        Object.keys(fields).forEach((current) => {
            data[current] = fields[current];
        });

        gridApi.current.redrawRows({ rowNodes: [rowNode] });
    }, []);

    const refreshColumnByColId = useCallback((colIds) => {
        if (colIds?.length === 0) return;

        let columns = colIds.reduce((arr, current) => {
            const column = gridColumnApi?.current?.getColumn?.(current);
            if (column) arr.push(column);
            return arr;
        }, []);

        gridApi.current?.refreshCells({ columns, force: true });
    }, []);

    // Child method
    useEffect(() => {
        onRef &&
            onRef({
                refresh,
                clearCheckboxes,
                updateRow,
                updateFavoriteRow: newGetFavoriteRows,
                getApi: () => gridApi.current,
                refreshColumnByColId,
            });
    }, [onRef, refresh, clearCheckboxes, updateRow, newGetFavoriteRows, refreshColumnByColId]);

    //Updates favoriteRows and drag rows when receiving new rowData when the table is !useLazyLoad
    useEffect(() => {
        if (!rowData || rowData?.length === 0) return;
        if (useLazyLoad) return;
        newGetFavoriteRows(rowData);
    }, [newGetFavoriteRows, rowData, useLazyLoad]);

    // Control the overlays to display
    useEffect(() => {
        // Sometimes this code is executed before gridApi exists so let's wait a tick
        setTimeout(() => {
            if (!gridApi?.current) return;
            if (isLoading) {
                setSuppressNoRowsOverlay(true);
                gridApi?.current?.showLoadingOverlay?.();
            } else {
                gridApi?.current?.sizeColumnsToFit?.();
                if (!useLazyLoad) {
                    if (rowData?.length > 0) {
                        gridApi.current.hideOverlay();
                    } else {
                        setSuppressNoRowsOverlay(false);
                        gridApi.current.showNoRowsOverlay();
                    }
                } else if (isLazyLoaded) {
                    if (immutableRowData.current && immutableRowData?.current?.length > 0) {
                        gridApi.current.hideOverlay();
                    } else {
                        setSuppressNoRowsOverlay(false);
                        gridApi.current.showNoRowsOverlay();
                    }
                }
            }
        });

        // Fix for dataGrid glitch that was showing an empty row before
        // loading the custom LoadingOverlay
        if (gridContext.current.totalData === 0 && useLazyLoad) {
            dataGridRef.current?.classList?.add(classes.isEmpty);
        } else {
            dataGridRef.current?.classList?.remove(classes.isEmpty);
        }
    }, [
        useLazyLoad,
        rowData,
        isLoading,
        gridApi,
        suppressNoRowsOverlay,
        isLazyLoaded,
        classes.isLoading,
        classes.isEmpty,
    ]);

    const setRowSelected = useCallback((activeRowId) => {
        if (!gridApi?.current) return;
        gridApi.current.deselectAll();
        const activeNode = gridApi.current.getRowNode(String(activeRowId));
        if (activeNode) activeNode.setSelected(true);
    }, []);

    useEffect(() => {
        setRowSelected(activeRowId);
    }, [activeRowId, setRowSelected]);

    setupCurrencyLocaleUser(context.locale);

    // This informs us about the current sort direction that the header column is displaying
    // >> so we can retrieve the correct sort to backend
    const getSortInfoForLazyLoad = useCallback((sortInfo) => {
        sortInfoForLazyLoad.current = sortInfo;
    }, []);

    //For lazyLoad we define the dataSource here
    const setGridDataSource = useCallback(() => {
        shouldHideGhostRows(true);
        const dataSource = {
            getRows: (params) => {
                let sortInfo = null;
                // Fast loading overlay
                // We show the loading overlay because when we execute the initTable,
                // >> the table suddenly has no data and thinks that is because has no result.
                // So it shows the noRowsOverlay, while we are retrieving the sorted data from backend.
                // This happens because the getRows has been triggered by the setSortModel and not because the scroll
                // >> so the table thinks that has to sort the data, but we do it through backend instead
                // As soon as has data again, the table itself will remove the showLoadingOverlay.
                if (!params.startRow) {
                    dataGridRef.current?.classList?.add(classes.isLoading);
                    shouldHideGhostRows(true);
                }

                gridContext.current.startRow = params.startRow;
                gridApi.current.showLoadingOverlay();

                if (sortInfoForLazyLoad.current) {
                    sortInfo = {
                        sortField:
                            sortInfoForLazyLoad.current.sortField ||
                            sortInfoForLazyLoad.current.colId,
                        sortDir: sortInfoForLazyLoad.current.sort,
                    };
                }

                setIsLazyLoaded(false);
                const previousTotal = gridContext.current.totalData;

                let startRow = 0;
                if (lazyLoadInfo?.current?.resetOffset) {
                    startRow = 0;
                    lazyLoadInfo.current = null;
                } else startRow = params.startRow;

                function innerGetTotal() {
                    // Only first time

                    if (
                        getTotal &&
                        startRow === 0 &&
                        (gridContext.current.hasShowTotal || forceGetTotal)
                    ) {
                        return getTotal()
                            .then((total) => {
                                gridContext.current.totalData = total;
                            })
                            .catch(() => {
                                gridContext.current.totalData = 0;
                            })
                            .then(() => {
                                publish(
                                    `${DATA_GRID_REFRESH_TOTAL}-${id}`,
                                    gridContext.current.totalData,
                                );
                            });
                    } else return Promise.resolve();
                }

                Promise.all([innerGetTotal(), initTable(pageSize, startRow, sortInfo)])
                    .then(([_, result]) => {
                        const { entities, total, lastRow } = result;

                        // Because "total" is only real in the first call. After that it's always 0.
                        if (!getTotal) {
                            if (entities.length > 0 && total) {
                                gridContext.current.totalData = total;
                            } else if (!entities?.length) gridContext.current.totalData = 0;
                        }

                        // so we keep immutableRowData updated
                        immutableRowData.current = [...entities];

                        // When adding sections with fullWidthRow, the table doesn't control well the total rows to print,
                        // >> so we control it using the grigContext.current.table data to force to not print more rows
                        // >> than necessary in the last pagination
                        if (useLazyWithSections) {
                            if (startRow === 0) gridContext.current.tableDataForLazySections = [];
                            const isLastPage = entities.length < pageSize;

                            if (isLastPage) {
                                params.successCallback(
                                    entities,
                                    gridContext.current.tableDataForLazySections.length +
                                        entities.length,
                                );
                            } else {
                                params.successCallback(entities);
                            }
                            gridContext.current.tableDataForLazySections = [
                                ...gridContext.current.tableDataForLazySections,
                                ...entities,
                            ];
                        } else if (infiniteScrolling) {
                            params.successCallback(entities, lastRow);
                        } else {
                            // Second param is lastRow and it should be provided when we know it
                            params.successCallback(
                                entities,
                                !getTotal ||
                                    entities.length < pageSize ||
                                    pageSize === gridContext.current.totalData
                                    ? getTotalWithFullWidthRows(
                                          entities,
                                          gridContext.current.totalData,
                                      )
                                    : undefined,
                            );
                        }
                    })
                    .catch((error) => {
                        if (error) console.error(error);
                        immutableRowData.current = [];
                        gridContext.current.totalData = 0;
                        params.failCallback();
                    })
                    .then(() => {
                        // Finally callback
                        if (getForceRedrawRows?.()) {
                            // This method is to force the table to ignore/forget cache memory in the rendering so,
                            // >> we ignore the result of the purgeInfiniteCache that ignored the redraw of several
                            // >> rows besides those were updated, but didn't reflected the change on the table
                            // This is very specific for the case where we have a table with fullWidthRows and useLazyLoad (inifinite scroll)
                            // >> and we need to refresh the table without a mounting the component again or populating it with a complete different data.
                            // In short. Without this, the table makes a refresh, but the fullWidthRows aren't displayed as fullWidthRows but normal rows.
                            gridApi?.current?.redrawRows();
                        }
                        shouldHideGhostRows(false);
                        dataGridRef.current?.classList?.remove(classes.isLoading);
                        setIsLazyLoaded(true);
                        newGetFavoriteRows();
                        newGetCheckedRows();
                        if (
                            !getTotal &&
                            (gridContext.current.hasShowTotal || forceGetTotal) &&
                            previousTotal !== gridContext.current.totalData &&
                            startRow === 0
                        ) {
                            publish(
                                `${DATA_GRID_REFRESH_TOTAL}-${id}`,
                                gridContext.current.totalData,
                            );
                        }
                    });
            },
        };
        gridApi.current.setDatasource(dataSource);
    }, [
        shouldHideGhostRows,
        initTable,
        pageSize,
        classes.isLoading,
        getTotal,
        id,
        useLazyWithSections,
        infiniteScrolling,
        getTotalWithFullWidthRows,
        getForceRedrawRows,
        newGetFavoriteRows,
        newGetCheckedRows,
        forceGetTotal,
    ]);

    const onReady = useCallback(
        (params) => {
            if (initOnReady) {
                if (useLazyLoad) {
                    setGridDataSource();
                } else initTable(pageSize);
            }
        },
        [initOnReady, useLazyLoad, initTable, setGridDataSource, pageSize],
    );

    // Main events
    const onGridReady = useCallback(
        (params) => {
            gridRef.current = params;
            gridApi.current = params.api;
            gridColumnApi.current = params.columnApi;

            addDropZones && addDropZones(params);
            onReady && onReady(params);
            if (useDragCustom) {
                dragObserver.current?.disconnect();
                dragObserver.current = addCustomDrag({
                    getCustomDragInfo,
                    immutableRowData: immutableRowData,
                    rowData,
                    dragPreview: customDragPreview,
                    previewKey,
                    canDrop,
                    onDragDrop,
                    dragTarget,
                    canDrag,
                });
            }
        },
        [
            addDropZones,
            onReady,
            useDragCustom,
            getCustomDragInfo,
            rowData,
            customDragPreview,
            previewKey,
            canDrop,
            onDragDrop,
            canDrag,
        ],
    );

    const onGridSizeChanged = useCallback((params) => {
        const clientWidth = gridWidthRef.current;
        gridWidthRef.current = params.clientWidth;
        if (params.clientWidth && clientWidth && params.clientwidth === clientWidth) return;
        try {
            setTimeout(() => {
                params?.api?.sizeColumnsToFit?.();
            });
        } catch (e) {
            console.error(e);
        }
    }, []);

    const onFirstDataRendered = useCallback((params) => {
        try {
            params?.api?.sizeColumnsToFit?.();
            apiRef.current = params.api;
        } catch (e) {
            console.error(e);
        }
    }, []);

    const moveInArray = useCallback((arr, fromIndex, toIndex) => {
        const element = arr[fromIndex];
        arr.splice(fromIndex, 1);
        arr.splice(toIndex, 0, element);
    }, []);

    const onRowDragEnter = useCallback(
        (event) => {
            if (showGhostWhenDrag) {
                const ghostRow = document.querySelector('.ag-dnd-ghost');
                if (ghostRow) ghostRow.classList.add('ag-dnd-ghost--visible');
            }
            isDragging.current = true;
            setDraggedRow(event.api, event.node, true);
            removeChildrenOnDrag(event.api, onCancelGroupRequests);

            immutableBeforeDrag.current = [];
            event.api.forEachNode((current) => {
                immutableBeforeDrag.current.push(current.data);
            });

            addToRowsBeforeDrag(immutableBeforeDrag.current);
            onDragEnter && onDragEnter(event);
        },
        [isDragging, onCancelGroupRequests, immutableBeforeDrag, onDragEnter, showGhostWhenDrag],
    );

    const onRowDragMove = useCallback(
        (event) => {
            const movingNode = event.node;
            const overNode = event.overNode;
            const rowNeedsToMove = movingNode !== overNode;
            if (rowNeedsToMove) {
                const movingData = movingNode?.data;
                const overData = overNode?.data;
                if (!overData) return;
                if (dragGroups?.groups && dragGroups?.groupsKey) {
                    const movingDataKey = movingData[dragGroups.groupsKey];
                    const group = dragGroups?.groups[movingDataKey];
                    if (movingDataKey && group && !group.includes(overData.id)) {
                        return;
                    }
                }
                const offset = gridApi.current.getFirstDisplayedRow();
                const fromIndex = immutableRowData.current.indexOf(movingData);
                let toIndex = immutableRowData.current.indexOf(overData) + offset;
                if (withoutOffset) toIndex = immutableRowData.current.indexOf(overData);
                const newRowData = immutableRowData.current.slice();
                moveInArray(newRowData, fromIndex, toIndex);
                immutableRowData.current = newRowData;
                rowMoved.current = true;
                gridContext.current.updateTableData(newRowData);
                gridApi.current.setRowData(newRowData);
                gridApi.current.clearFocusedCell();
            }
        },
        [dragGroups, moveInArray, withoutOffset],
    );

    const onRowDragEnd = useCallback(
        (event) => {
            if (rowMoved.current && onDragEnd) {
                const data = { rowDataBefore: immutableBeforeDrag.current };
                addRequestToQueue(() => onDragEnd(event, data));

                if (!isQueueProcessing.current) {
                    isQueueProcessing.current = true;
                    processDragQueue(event, immutableRowData, isQueueProcessing);
                }
            }
            setDraggedRow(event.api, event.node);
            rowMoved.current = false;

            if (columnsRefreshOnDragEnd) {
                let columns = columnsRefreshOnDragEnd.filter((current) => {
                    return gridColumnApi.current.getColumn(current);
                });
                gridApi.current.refreshCells({ columns, force: true });
            }
        },
        [onDragEnd, columnsRefreshOnDragEnd],
    );

    const onRowDragLeave = useCallback((event) => {
        isDragging.current = false;
        setDraggedRow(event.api, event.node);
    }, []);

    const onColumnMoved = useCallback((event) => {
        if (!event?.column?.colId) return;
        columnMovedId.current = event.column.colId;
    }, []);

    const onColumnPinned = useCallback(
        (event) => {
            savePinnedColumns && savePinnedColumns(event.column);
        },
        [savePinnedColumns],
    );

    const onColumnResized = useCallback(
        (event) => {
            if (!saveColumnSize || !event?.finished) return;
            if (event?.source === 'sizeColumnsToFit') return;

            event.columns?.forEach((column) => {
                if (column?.colId === 'fmFieldSelectorSpacer') return;
                if (!column?.colId) return;

                saveColumnSize(column.colId, column.actualWidth);
            });
        },
        [saveColumnSize],
    );

    const onRowSelected = useCallback((event) => {
        if (event?.node?.isSelected?.()) {
            oldInitialActiveRowId.current = event.node.id;
        }
    }, []);

    const onViewportChanged = useCallback(
        (event) => {
            // To be executed when the table has to load with a row selected
            if (
                activeRowId &&
                (!oldInitialActiveRowId?.current || oldInitialActiveRowId.current !== activeRowId)
            ) {
                setRowSelected(activeRowId);
            }
        },
        [activeRowId, setRowSelected],
    );

    const onRowClicked = useCallback(
        (event) => {
            if (isDragging.current) {
                isDragging.current = false;
                return;
            }
            onClickRow && onClickRow(event);
        },
        [onClickRow],
    );

    const getIsAllChecked = useCallback((context, colId) => {
        const checkedInColumn = context?.checkboxData?.[colId]?.rows || null;
        const checkedList =
            checkedInColumn &&
            Object.keys(checkedInColumn).filter((current) => {
                return checkedInColumn[current];
            });

        // checkedList contains all the rows checked of the loaded rows
        if (checkedList.length === context.totalData) {
            context.checkboxData[colId].areAllChecked = true;
        } else context.checkboxData[colId].areAllChecked = false;
    }, []);

    const getIsRowChecked = useCallback(
        (colId, rowId) => {
            let isChecked = false;
            const context = gridContext.current;
            if (useLazyLoad) {
                if (context.checkboxData[colId].rows[rowId]) {
                    isChecked = context.checkboxData[colId].rows[rowId];
                } else if (
                    typeof context.checkboxData[colId].rows?.[rowId] === 'boolean' &&
                    !context.checkboxData[colId].rows?.[rowId]
                ) {
                    isChecked = false;
                } else if (context.checkboxData[colId].lazyAreAllChecked) {
                    isChecked = true;
                } else isChecked = false;
            } else {
                isChecked = context.checkboxData[colId].rows[rowId] || false;
            }
            return isChecked;
        },
        [useLazyLoad],
    );

    // Here we build the resultant checkboxData after checkbox click
    const onClickCheckbox = useCallback(
        (checkboxCallback) => {
            return ({ id, colId, boolean, context, column, rowIndex, data }) => {
                if (column && !colId) return;
                else if (!colId && !id) return;

                let totalBlocked = 0;
                if (!context?.checkboxData) context.checkboxData = {};
                if (!context?.checkboxData?.[colId]) {
                    context.checkboxData[colId] = {};
                    context.checkboxData[colId].rows = {};
                }
                if (column) {
                    let newCheckboxes = {};
                    gridApi.current.forEachNode((node) => {
                        if (disabledCheckboxes?.[colId]?.[node.id]) return;
                        if (node?.data?.oldSelected === '1') {
                            newCheckboxes[node.id] = true;
                            return;
                        }
                        if (
                            column.colDef.cellRendererParams?.checkbox?.isDisabled &&
                            column.colDef.cellRendererParams?.checkbox?.isDisabled(
                                colId,
                                id,
                                node,
                            ) &&
                            column.colDef.cellRendererParams?.checkbox?.noCheckedWhenDisabled
                        ) {
                            totalBlocked++;
                            return;
                        }
                        newCheckboxes[node.id] = boolean;
                    });
                    context.checkboxData[colId].areAllChecked = boolean;
                    context.checkboxData[colId].rows = newCheckboxes;
                    if (useLazyLoad) {
                        context.checkboxData[colId].lazyAreAllChecked = boolean;
                    }
                } else {
                    context.checkboxData[colId].rows[id] = boolean;

                    if (useLazyLoad) {
                        getIsAllChecked(context, colId);
                        context.checkboxData[colId].lazyAreAllChecked =
                            context.checkboxData[colId].lazyAreAllChecked || false;
                    } else if (!boolean) {
                        context.checkboxData[colId].areAllChecked = false;
                    } else {
                        getIsAllChecked(context, colId);
                    }
                }

                const areAllChecked = context?.checkboxData[colId]?.areAllChecked || null;

                checkboxCallback &&
                    checkboxCallback({
                        id: id || null,
                        colId,
                        isColumn: !!column,
                        value: boolean,
                        checked: context.checkboxData,
                        rowIndex,
                        data,
                        total: areAllChecked ? context.totalData - totalBlocked : 0,
                    });

                if (column) {
                    refreshCheckboxes(colId, areAllChecked);
                } else gridApi.current?.refreshHeader();
            };
        },
        [useLazyLoad, disabledCheckboxes, getIsAllChecked, refreshCheckboxes],
    );

    const getIsRowFavorite = useCallback(
        (colId, rowId) => {
            let isFavorite = false;
            const context = gridContext.current;
            if (useLazyLoad) {
                if (context.favoriteData[colId].rows[rowId]) {
                    isFavorite = context.favoriteData[colId].rows[rowId];
                } else if (
                    typeof context.favoriteData[colId].rows?.[rowId] === 'boolean' &&
                    !context.favoriteData[colId].rows?.[rowId]
                ) {
                    isFavorite = false;
                } else isFavorite = false;
            } else {
                isFavorite = context.favoriteData[colId].rows[rowId] || false;
            }
            return isFavorite;
        },
        [useLazyLoad],
    );

    const onClickFavorite = useCallback((favoriteCallback) => {
        return ({ id, colId, boolean, context, rowIndex, data }) => {
            if (!colId && !id) return;

            if (!context?.favoriteData) context.favoriteData = {};
            if (!context?.favoriteData?.[colId]) {
                context.favoriteData[colId] = {};
                context.favoriteData[colId].rows = {};
            }

            context.favoriteData[colId].rows[id] = boolean;

            favoriteCallback &&
                favoriteCallback({
                    id: id || null,
                    colId,
                    value: boolean,
                    checked: context.favoriteData,
                    rowIndex,
                    data,
                });
        };
    }, []);

    const newFullWidthCellRender = useMemo(() => {
        if (useFullWidthDivider) return FullWidthDividerCell;
        else {
            return fullWidthCellRenderer && !onCancelGroupRequests
                ? fullWidthCellRenderer
                : LoadingCell;
        }
    }, [fullWidthCellRenderer, onCancelGroupRequests, useFullWidthDivider]);

    const loadingIsFullWidthCell = useCallback((rowNode) => {
        return rowNode?.data?.isFullWidth || false;
    }, []);

    const isFullWidthRow = useCallback(
        ({ rowNode }) => {
            if (customIsFullWidthCell)
                return customIsFullWidthCell(rowNode) || loadingIsFullWidthCell(rowNode);
            else return loadingIsFullWidthCell(rowNode);
        },
        [customIsFullWidthCell, loadingIsFullWidthCell],
    );

    const finalRowHeight = props.rowHeight || defaultOptions.rowHeight;

    const getRowHeight = useCallback(
        (params) => {
            return params?.data?.isLoader ? 150 : finalRowHeight;
        },
        [finalRowHeight],
    );

    const customRowCells = useMemo(
        () => ({
            textCell: TextCell,
            textTwoValuesCell: TextTwoValuesCell,
            avatarCell: AvatarCell,
            dateCell: DateCell,
            currencyCell: CurrencyCell,
            valuePercentageCell: ValuePercentageCell,
            percentageCell: PercentageCell,
            percentageTextCell: PercentageTextCell,
            booleanCell: BooleanCell,
            booleanIconCell: BooleanIconCell,
            numberCell: NumberCell,
            rowNumberCell: RowNumberCell,
            toggleCell: ToggleCell,
            tagCell: TagCell,
            iconCell: IconCell,
            multiValueCell: MultiValueCell,
            stepCell: StepCell,
            filterCell: FilterCell,
            fullWidthCellRenderer: newFullWidthCellRender,
            textFilter: TextFilter,
            headerTextCell: HeaderTextCell,
            headerIconCell: HeaderIconCell,
        }),
        [newFullWidthCellRender],
    );

    const wrappedChangeSortDirection = useCallback(
        (props) => {
            changeSortDirection && changeSortDirection(props);
        },
        [changeSortDirection],
    );

    const newChangeSortDirection = useCallback((getSortInfoForLazyLoad) => {
        return (sortModel) => {
            if (getSortInfoForLazyLoad) getSortInfoForLazyLoad(sortModel);
        };
    }, []);

    const finalColumnDefs = useMemo(
        () =>
            columnDefs.map((current, idx) => {
                // If we have actions but the current column doesn't use a customCellRenderer, we set one
                // because the actions are controlled from the Base cell in the customCells
                if (
                    actions?.length > 0 &&
                    (!current.cellRenderer ||
                        (!customRowCells[current.cellRenderer] &&
                            !components[current.cellRenderer]))
                ) {
                    current.cellRenderer = 'textCell';
                }

                if (!current.cellRendererParams) current.cellRendererParams = {};
                current.cellRendererParams = {
                    ...current.cellRendererParams,
                    ...context,
                };

                if (!current.headerComponentParams) current.headerComponentParams = {};

                if (useDragColumns && current.rowDrag) {
                    current.lockPosition = true;
                    current.suppressMovable = true;
                } else if (useDragColumns && !current.lockPosition && !current.rowDrag) {
                    if (!current.headerComponent) current.headerComponent = 'headerTextCell';
                    if (!current.suppressMovable) {
                        current.headerComponentParams = {
                            ...current.headerComponentParams,
                            useDragColumns: true,
                        };
                    }
                }

                if (useDragRows) {
                    current.headerComponentParams = {
                        ...current.headerComponentParams,
                        useDragRows: true,
                    };
                }

                if (useSort) {
                    if (!current.headerComponent) current.headerComponent = 'headerTextCell';
                    current.sortable = current.hasOwnProperty('sortable') ? current.sortable : true;
                }

                if (useSort && changeSortDirection && current.sortField) {
                    current.headerComponentParams.sortField = current.sortField;
                    current.headerComponentParams.changeSortDirection = wrappedChangeSortDirection;
                }

                if (useSort) {
                    if (getInitialSortInfo) {
                        const initialSort = getInitialSortInfo();
                        if (current.colId === initialSort?.sortField)
                            current.sort = initialSort.sortDirection;
                        current.headerComponentParams.getInitialSortInfo = getInitialSortInfo;
                    }
                    if (useLazyLoad) {
                        current.headerComponentParams.getSortInfoForLazyLoad =
                            newChangeSortDirection(getSortInfoForLazyLoad);
                    }
                }

                if (!current.minWidth) {
                    current.minWidth =
                        current?.width < defaultColumnMinWidth
                            ? current.width
                            : defaultColumnMinWidth;
                }

                if (current?.headerComponentParams?.showTotal) {
                    gridContext.current.hasShowTotal = true;
                }

                if (current?.headerComponentParams?.checkbox?.onChange) {
                    const onChange = current.headerComponentParams.checkbox.onChange;
                    delete current.headerComponentParams.checkbox.onChange;
                    current.headerComponentParams.checkbox.onClick = onClickCheckbox(onChange);
                }
                if (current?.cellRendererParams?.checkbox?.onChange) {
                    const onChange = current.cellRendererParams.checkbox.onChange;
                    delete current.cellRendererParams.checkbox.onChange;
                    current.cellRendererParams.checkbox.onClick = onClickCheckbox(onChange);
                    current.cellRendererParams.checkbox.getIsRowChecked = getIsRowChecked;
                }
                if (current?.cellRendererParams?.favorite?.onChange) {
                    const onChange = current.cellRendererParams.favorite.onChange;
                    delete current.cellRendererParams.favorite.onChange;
                    current.cellRendererParams.favorite.onClick = onClickFavorite(onChange);
                    current.cellRendererParams.favorite.getIsRowFavorite = getIsRowFavorite;
                }

                current.headerClass = current.headerClass
                    ? `${current.headerClass} w5-header`
                    : `w5-header`;

                if (current.headerComponent) {
                    current.headerClass = `${current.headerClass} w5-header-custom-cell`;
                }

                const align = current.cellRendererParams.align;
                const alignHeader = current.cellRendererParams.alignHeader;

                if (alignHeader)
                    current.headerClass = `${current.headerClass} w5-header--${alignHeader}`;
                else if (align) current.headerClass = `${current.headerClass} w5-header--${align}`;

                const removeHover = current.headerComponentParams.removeHover;
                if (!removeHover) current.headerClass = `${current.headerClass} w5-header--hover`;

                current.cellClassRules = {
                    'w5-cell-container': function (params) {
                        return true;
                    },
                    // Under this class we can edit native classes of the
                    // >> table without breaking the styles of the native cellRenderer
                    'w5-cell-container-custom-cell': function (params) {
                        if (current.cellRenderer) return true;
                        else return false;
                    },
                    'w5-cell-container-drag': function (params) {
                        return useDragRows;
                    },
                    'w5-cell-container-no-drag': function (params) {
                        return !useDragRows;
                    },
                    'w5-cell-container--dragging': function (params) {
                        if (draggedRow) {
                            return (
                                draggedRow?.data?.id &&
                                params?.data?.id &&
                                draggedRow.data.id === params.data.id
                            );
                        } else return false;
                    },
                    'w5-cell-container-lazy-drag-custom': function (params) {
                        return useDragCustom;
                    },
                    'w5-cell-container--no-border-left': function (params) {
                        return params?.colDef?.cellRendererParams?.isLast || false;
                    },
                    'w5-cell-container--no-border-right': function (params) {
                        return params?.colDef?.cellRendererParams?.isFirst || false;
                    },
                    'w5-cell-container--no-border-left-and-right': function (params) {
                        if (
                            !params?.colDef?.cellRendererParams?.isFirst &&
                            !params?.colDef?.cellRendererParams?.isLast &&
                            params.colDef.cellRenderer === 'stepCell'
                        )
                            return true;
                        else return false;
                    },
                    ...current.cellClassRules,
                };

                // Limiting the width to avoid that a column cannot be resized later.
                if (current.pinned && gridContext.current?.tableRef.current?.eGridDiv) {
                    const offsetWidth =
                        gridContext?.current?.tableRef?.current?.eGridDiv?.offsetWidth || 100;
                    const limit = offsetWidth * 0.75;
                    if (current.width > limit) current.width = limit;
                }

                // Removing duplicates
                const classSet = new Set(current.headerClass.split(' '));
                current.headerClass = Array.from(classSet).join(' ');

                // Fixing last column width when actions set
                if (columnDefs?.length === idx + 1 && actions?.length) {
                    current.suppressSizeToFit = false;
                    current.resizable = false;
                }
                return current;
            }),
        [
            actions,
            changeSortDirection,
            columnDefs,
            components,
            context,
            customRowCells,
            getInitialSortInfo,
            getIsRowChecked,
            getIsRowFavorite,
            getSortInfoForLazyLoad,
            wrappedChangeSortDirection,
            newChangeSortDirection,
            onClickCheckbox,
            onClickFavorite,
            useDragColumns,
            useDragRows,
            useDragCustom,
            useLazyLoad,
            useSort,
        ],
    );

    const onDragStopped = useCallback(
        (event) => {
            if (saveColumnsOrder && columnMovedId.current) {
                const columnOrder = gridColumnApi.current.getAllDisplayedColumns().map((column) => {
                    return column.getColId();
                });
                const indexOfMoved = columnOrder.indexOf(columnMovedId.current);
                const columnAfter = columnOrder[indexOfMoved - 1] || null;
                const reorderedColumn = columnOrder[indexOfMoved];
                saveColumnsOrder(columnAfter, reorderedColumn);
                if (actions?.length) {
                    // fix last column props
                    const columns = gridApi.current.getColumnDefs();
                    columns.forEach((column, idx) => {
                        if (idx + 1 === columns?.length) {
                            column.resizable = false;
                            column.suppressSizeToFit = false;
                            column.width = undefined;
                        } else {
                            const originalColumn = columnDefs.find(
                                (def) => def.colId === column.colId,
                            );
                            column.suppressSizeToFit = true;
                            column.resizable =
                                originalColumn.hasOwnProperty('resizable') &&
                                reorderedColumn !== column.colId
                                    ? originalColumn.resizable
                                    : true;
                        }
                    });
                    gridApi.current.setColumnDefs(columns);
                }
            }
        },
        [actions?.length, columnDefs, saveColumnsOrder],
    );

    const onRowDataUpdated = useCallback(() => {
        if (forceRowUpdating) {
            gridApi.current?.refreshCells({ force: true });
        }
    }, [forceRowUpdating]);

    const options = useMemo(() => {
        let newColumnDefs = finalColumnDefs;
        let affectedByDrag = {
            suppressRowDrag: true,
            getRowId: getRowNodeId ? ({ data }) => getRowNodeId(data) : ({ data }) => data?.id,
        };

        if (useDragRows) {
            if (!affectedByDrag.getRowId) {
                affectedByDrag.getRowId = function (data) {
                    return data.id;
                };
            }
            affectedByDrag.animateRows = true;
            affectedByDrag.suppressRowDrag = false;
            affectedByDrag.rowDragManaged = false;
        }

        if (fieldSelector) {
            // Fake column for fieldSelector space
            // This avoids the last column to be hidden below the 3 dots menu in the top right of the table
            newColumnDefs = [
                ...finalColumnDefs,
                {
                    colId: 'fmFieldSelectorSpacer',
                    minWidth: 44,
                    maxWidth: 44,
                    resizable: false,
                    headerClass: 'w5-header w5-header__field-selector',
                    cellClass: 'w5-cell__field-selector',
                },
            ];
        }

        const newOptions = {
            ...defaultOptions,
            rowData: !useLazyLoad ? rowData : null,
            rowSelection: 'single',
            rowModelType: useLazyLoad ? 'infinite' : null,
            cacheBlockSize: useLazyLoad ? pageSize : null,
            rowBuffer: 0,
            // 0 avoids a visible empty placeholder row while the debounced getRows runs after sort/filter (infinite model).
            blockLoadDebounceMillis: useLazyLoad ? 0 : 300,
            maxConcurrentDatasourceRequests: 3,
            layoutInterval: 200,
            tooltipShowDelay: 200,

            components: {
                ...customRowCells,
                ...vanillaComponents,
                loadingOverlay: (...props) => (
                    <LoadingOverlay
                        {...props}
                        id={id}
                        rowData={immutableRowData.current}
                        useLoaderPlaceholder={useLoaderPlaceholder}
                        placeholderOptions={placeholderOptions}
                        loaderRowHeight={loaderPlaceholderRowHeight || finalRowHeight}
                        loaderRows={loaderPlaceholderRows}
                        loaderColumns={loaderPlaceholderColumns}
                        useLoaderFullHeight={useLoaderPlaceholderFullHeight}
                        gridContext={gridContext}
                    />
                ),
                noRowsOverlay: customEmptyViewComponent || NoRowsOverlay,
                ...components,
            },
            suppressNoRowsOverlay,
            loadingOverlayComponent: 'loadingOverlay',
            noRowsOverlayComponent: 'noRowsOverlay',
            noRowsOverlayComponentParams: {
                hasError,
                isLoading,
                emptyViewProps,
                errorViewProps,
                headerHeight: 40,
            },
            defaultColDef: {
                ...defaultOptions.defaultColDef,
                ...defaultColDef,
            },
            columnDefs: newColumnDefs,
            fullWidthCellRenderer: 'fullWidthCellRenderer',
            isFullWidthRow,
            getRowHeight,
            rowClass: ['w5-row', rowClass, onClickRow && 'w5-row--click'].join(' '),
            rowClassRules: {
                'w5-row-children': function (params) {
                    return params?.data?.isChildren;
                },
                'w5-row-loader': function (params) {
                    return params?.data?.isLoader;
                },
                'w5-row-group--open': function (params) {
                    if (params?.data?.isGroup && params?.data?.isGroupOpen) {
                        return true;
                    }
                },
                'w5-row-group--close': function (params) {
                    if (params?.data?.isGroup && !params?.data?.isGroupOpen) {
                        return true;
                    }
                },
                'w5-row-group--open__no-events': function (params) {
                    if (
                        params?.data?.isGroup &&
                        params?.data?.isGroupOpen &&
                        params?.data?.isLoading
                    ) {
                        return true;
                    }
                },
                'w5-row-full-width': function (params) {
                    return isFullWidthRow({ rowNode: params.node }) || false;
                },
                ...rowClassRules,
            },
            onRowDragEnter,
            onRowDragMove,
            onRowDragEnd,
            onRowDragLeave,
            onRowClicked,
            onDragStopped,
            onColumnMoved,
            onColumnPinned,
            onColumnResized,
            onViewportChanged,
            onRowSelected,
            onSortChanged: useLazyLoad ? onLazyLoadSortChanged : undefined,
            getRowId: getRowNodeId ? ({ data }) => getRowNodeId(data) : null,
            onRowDataUpdated,
            ...props,
            ...affectedByDrag,
        };

        if (dataSource) {
            newOptions.rowModelType = 'infinite';
            newOptions.datasource = dataSource;
        }

        if (useDragColumns) {
            newOptions.suppressMovableColumns = false;
        }

        return newOptions;
    }, [
        finalColumnDefs,
        props,
        useDragRows,
        fieldSelector,
        useLazyLoad,
        rowData,
        pageSize,
        customRowCells,
        vanillaComponents,
        customEmptyViewComponent,
        components,
        suppressNoRowsOverlay,
        hasError,
        isLoading,
        emptyViewProps,
        errorViewProps,
        defaultColDef,
        isFullWidthRow,
        getRowHeight,
        rowClass,
        onClickRow,
        rowClassRules,
        onRowDragEnter,
        onRowDragMove,
        onRowDragEnd,
        onRowDragLeave,
        onRowClicked,
        onDragStopped,
        onColumnMoved,
        onColumnPinned,
        onColumnResized,
        onViewportChanged,
        onRowSelected,
        onLazyLoadSortChanged,
        getRowNodeId,
        onRowDataUpdated,
        dataSource,
        useDragColumns,
        id,
        useLoaderPlaceholder,
        placeholderOptions,
        loaderPlaceholderRowHeight,
        finalRowHeight,
        loaderPlaceholderRows,
        loaderPlaceholderColumns,
        useLoaderPlaceholderFullHeight,
    ]);

    useEffect(() => {
        if (
            gridRef &&
            gridRef.current &&
            gridRef.current.api &&
            gridRef.current.api.setInfiniteRowCount &&
            infiniteInitialRowCount
        ) {
            gridRef.current.api.setInfiniteRowCount(infiniteInitialRowCount);
        }

        return () => dragObserver.current?.disconnect();
    }, [infiniteInitialRowCount]);

    useEffect(() => {
        return () => {
            onCancelGroupRequests && onCancelGroupRequests();
        };
    }, [onCancelGroupRequests]);

    // Convert the grid columns schema to the format expected by ModalOptions
    const getModalOptionsSchema = useCallback(
        (groupedColumns) => {
            let newMappedSchema = [];

            if (groupedColumns?.length > 0) {
                newMappedSchema = groupedColumns.map((item) => {
                    const options = item.fields
                        .filter((option) => !option.suppressFromFieldSelector)
                        .map((option) => ({
                            id: option.colId,
                            label: option.headerName,
                        }));

                    return {
                        label: item.label || t('label_fields'),
                        id: item.label || t('label_fields'),
                        options: options,
                    };
                });
            }

            setMappedSchema([...newMappedSchema]);
        },
        [t],
    );

    const onChangeSelectedFields = useCallback((newSelectedFields) => {
        setSelectedFields({ ...newSelectedFields });
    }, []);

    const getSelectedFields = useCallback(
        (mappedSchema, hiddenColumns) => {
            const newSelectedFields = mappedSchema.reduce((obj, group) => {
                const options = group.options.reduce((fields, field) => {
                    if (hiddenColumns[field.id] && !hiddenColumns[field.id].hide)
                        fields.push(field.id);
                    return fields;
                }, []);

                obj[group.id] = {
                    isSelected:
                        (options.length > 0 && options.length === group.options.length) || false,
                    options,
                };

                return obj;
            }, {});

            oldSelectedFields.current = JSON.parse(JSON.stringify(newSelectedFields));
            onChangeSelectedFields(newSelectedFields);
        },
        [onChangeSelectedFields],
    );

    const { onCancel: handleCloseModalOptions, ...otherModalOptionsProps } = modalOptionsProps;

    const handleCancelExportModal = useCallback(() => {
        handleCloseModalOptions && handleCloseModalOptions(false);
        setSelectedFields(JSON.parse(JSON.stringify(oldSelectedFields.current)));
    }, [handleCloseModalOptions]);

    const handleConfirmExportModal = useCallback(
        (selectedFields) => {
            if (Object.keys(selectedFields).length > 0 && mappedSchema.length > 0) {
                oldSelectedFields.current = JSON.parse(JSON.stringify(selectedFields));

                const selectedColumns = Object.values(selectedFields).reduce(
                    (arr, { options }) => [...arr, ...options],
                    [],
                );

                const newSelectedFields = mappedSchema
                    .map((optionGroup) => optionGroup.options)
                    .flat()
                    .reduce(
                        (obj, item) => ({
                            ...obj,
                            [item.id]: selectedColumns.includes(item.id),
                        }),
                        {},
                    );

                const newHiddenColumns = Object.entries(newSelectedFields).reduce(
                    (fields, field) => {
                        const [column, visible] = field;
                        gridColumnApi.current.setColumnVisible(column, visible);
                        fields[column] = { ...hiddenColumns[column], hide: !visible };
                        return fields;
                    },
                    {},
                );

                saveVisibleColumns(newSelectedFields);
                setHiddenColumns(newHiddenColumns);
            }
            handleCloseModalOptions(false);
        },
        [handleCloseModalOptions, hiddenColumns, mappedSchema, saveVisibleColumns],
    );

    useEffect(() => {
        if (isFirstLoad.current && fieldSelectorProps?.groupedColumns?.length > 0) {
            getModalOptionsSchema(fieldSelectorProps.groupedColumns);
            isFirstLoad.current = false;
        }

        if (hiddenColumns && Object.keys(hiddenColumns).length > 0) {
            getSelectedFields(mappedSchema, hiddenColumns);
        }
    }, [fieldSelectorProps, getModalOptionsSchema, getSelectedFields, hiddenColumns, mappedSchema]);

    return (
        <div id={id} className={rootClassName} ref={dataGridRef}>
            {actions && actions?.length > 0 && (
                <Actions
                    actions={actions}
                    hideActions={hideActions}
                    hoveredRowInfo={hoveredRowInfo}
                    id={id}
                    dataGridRef={dataGridRef.current}
                    rowData={rowData}
                    isLazyLoaded={isLazyLoaded}
                    intercomRef={intercomRef}
                    isLoading={isLoading}
                    forceActionsRefresh={forceActionsRefresh}
                />
            )}
            {fieldSelector && (
                <MenuOptions
                    tooltipMenuContent={tooltipMenuContent}
                    fieldSelector={fieldSelector}
                    fieldSelectorProps={fieldSelectorProps}
                />
            )}
            <ToolTip />
            <Popover />
            <AgGridReact
                ref={gridContext.current.tableRef}
                onGridReady={onGridReady}
                onGridSizeChanged={onGridSizeChanged}
                onFirstDataRendered={onFirstDataRendered}
                infiniteInitialRowCount={infiniteInitialRowCount}
                isLoading={isLoading}
                context={gridContext.current}
                {...options}
            ></AgGridReact>
            {Object.entries(otherModalOptionsProps).length > 0 && (
                <ModalOptions
                    schema={mappedSchema}
                    selected={selectedFields}
                    onCancel={handleCancelExportModal}
                    onChangeSelected={onChangeSelectedFields}
                    onConfirm={handleConfirmExportModal}
                    {...otherModalOptionsProps}
                />
            )}
        </div>
    );
}

DataGrid.propTypes = {
    className: PropTypes.string,
};

export default React.memo(DataGrid);

let draggedRow = null;

function setDraggedRow(api, rowNode, isDragging) {
    draggedRow = (isDragging && rowNode) || null;
    let rowsToRefresh = [rowNode];
    refreshDraggedRow(api, rowsToRefresh);
}

function refreshDraggedRow(api, rowsToRefresh) {
    const params = {
        rowNodes: rowsToRefresh,
        force: true,
    };
    api.refreshCells(params);
}

function removeChildrenOnDrag(api, onCancelGroupRequests) {
    onCancelGroupRequests && onCancelGroupRequests();
    api.forEachNode((current) => {
        if (onCancelGroupRequests) {
            if (current?.data?.isLoader) {
                api.applyTransaction({ remove: [current.data] });
            } else if (current?.data?.isGroupOpen) {
                //can be open because isLoading but not having children yet
                delete current.data.isGroupOpen;

                if (current?.data?.children?.length > 0) {
                    api.applyTransaction({ remove: current.data.children });
                    delete current.data.children;
                }

                api.applyTransaction({ update: [current.data] });
            }
        } else {
            if (current?.data?.isGroupOpen && current?.data?.children?.length > 0) {
                api.applyTransaction({ remove: current.data.children });
                delete current.data.children;
                delete current.data.isGroupOpen;
                api.applyTransaction({ update: [current.data] });
            }
        }
    });
}

function addCustomDrag({
    getCustomDragInfo,
    immutableRowData,
    rowData,
    dragPreview,
    previewKey,
    canDrop,
    onDragDrop,
    dragTarget,
    canDrag,
}) {
    const targetNode = document.querySelector('.ag-center-cols-container');
    const config = { childList: true };

    const rows = document.querySelectorAll('.w5-row');
    for (let row of rows) {
        addDragEvents({
            row,
            getCustomDragInfo,
            immutableRowData,
            rowData,
            dragPreview,
            previewKey,
            canDrop,
            onDragDrop,
            dragTarget,
            canDrag,
        });
    }

    const callback = (mutationsList, observer) => {
        mutationsList.forEach((mutation) => {
            if (mutation.type !== 'childList') return;
            mutation.addedNodes.forEach((node) => {
                addDragEvents({
                    row: node,
                    getCustomDragInfo,
                    immutableRowData,
                    rowData,
                    dragPreview,
                    previewKey,
                    canDrop,
                    onDragDrop,
                    dragTarget,
                    canDrag,
                });
            });
        });
    };

    const observer = new MutationObserver(callback);
    observer.observe(targetNode, config);
    return observer;
}

function createDefaultDragPreview(event, previewElement, data, previewKey) {
    const previewStyles = {
        width: '125px',
        height: '35px',
        padding: '0px 10px',
        borderRadius: '2px',
        backgroundColor: '#5a89fd',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: '"Sage Text"',
        fontSize: '13px',
    };
    Object.assign(previewElement.style, previewStyles);

    const previewText = document.createElement('div');
    const previewTextStyles = {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: 'white',
    };
    Object.assign(previewElement.style, previewTextStyles);
    previewText.innerHTML = data.name || data[previewKey];
    previewElement.appendChild(previewText);

    document.body.appendChild(previewElement);
    event.dataTransfer.setDragImage(previewElement, 0, 0);
}

function addDragEvents({
    row,
    rowData,
    getCustomDragInfo,
    immutableRowData,
    dragPreview,
    previewKey,
    canDrop,
    onDragDrop,
    dragTarget,
    canDrag,
}) {
    if (row.getAttribute('draggable')) return;
    const rowIndex = row.getAttribute('row-index');
    const data = immutableRowData.current?.[rowIndex] || rowData?.[rowIndex];
    if ((canDrag && canDrag(data)) || !canDrag) {
        row.setAttribute('draggable', true);
        row.addEventListener(
            'dragstart',
            function (event) {
                const previewElement = document.createElement('div');
                previewElement.setAttribute('id', 'w5-drag-preview');
                const rowId = row.getAttribute('row-id');
                const rowData = immutableRowData.current?.[row.getAttribute('row-index')];

                event.dataTransfer.setData('text/plain', JSON.stringify(rowData));
                event.dataTransfer.setData('id', rowId);

                if (dragPreview) {
                    const DragPreview = dragPreview;
                    const preview = renderToStaticMarkup(
                        <DragPreview data={rowData} previewKey={previewKey} />,
                    );
                    previewElement.innerHTML = preview;
                    document.body.appendChild(previewElement);
                    event.dataTransfer.setDragImage(previewElement, 0, 0);
                } else {
                    createDefaultDragPreview(event, previewElement, rowData, previewKey);
                }

                event.dataTransfer.dropEffect = 'move';
                getCustomDragInfo?.({ dragged: rowId });
            },
            false,
        );
    }

    row.addEventListener(
        'dragend',
        function (event) {
            const previewElement = document.getElementById('w5-drag-preview');
            previewElement?.remove();
            row.classList.remove('w5-drag-over');
            getCustomDragInfo?.({ dragged: null });
        },
        false,
    );

    row.addEventListener(
        'dragenter',
        function (event) {
            event.preventDefault();
            event.stopPropagation();
            if (event.currentTarget !== dragTarget.current) dragTarget.current = null;

            const rowData = immutableRowData.current?.[row.getAttribute('row-index')];
            const allowDrop = canDrop ? canDrop(rowData) : true;
            if (allowDrop) {
                row.classList.add('w5-drag-over');
            }
        },
        false,
    );

    row.addEventListener(
        'dragleave',
        function (event) {
            event.preventDefault();
            event.stopPropagation();
            const inRow = event.fromElement?.closest('.ag-row');
            if (event.currentTarget === dragTarget.current && inRow) return;
            if (!inRow) {
                dragTarget.current = event.currentTarget;
            } else dragTarget.current = event.currentTarget;
            row.classList.remove('w5-drag-over');
            getCustomDragInfo?.({ target: null });
        },
        false,
    );

    row.addEventListener(
        'drop',
        function (event) {
            const targetRow = event?.target?.closest('.ag-row');
            targetRow?.classList.remove('w5-drag-over');
            try {
                onDragDrop &&
                    onDragDrop(
                        JSON.parse(event.dataTransfer.getData('text/plain')),
                        immutableRowData.current?.[targetRow.getAttribute('row-index')],
                    );
            } catch (e) {
                console.error(e);
            }
        },
        false,
    );

    row.addEventListener(
        'dragover',
        function (event) {
            const rowId = row.getAttribute('row-id');
            const rowData = immutableRowData.current?.[row.getAttribute('row-index')];
            const allowDrop = canDrop ? canDrop(rowData) : true;
            if (allowDrop) {
                event.preventDefault();
                getCustomDragInfo?.({ target: rowId });
            }

            return false;
        },
        false,
    );
}
